import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSelectedLanguageContext } from '~/components/SelectedLanguage';
import { authStoreStateSelector } from '~/selectors';
import { AppModules, PathModules } from '~/services/Permissions';
import { AppDispatch } from '~/store';
import { redirectToOrigin } from '~/utils/helper';

import { fetchAuthenticatedUserDetails } from '../../slice';

import { authenticatedUserIsAdmin } from './authenticatedUserIsAdmin';
import { pathModuleIsEnabled } from './pathModuleIsEnabled';

export interface UsePrivateRouteOutput {
  isAuthenticated: boolean;
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;
}

export const usePrivateRoute = (
  moduleRestrict: boolean,
  path?: readonly string[] | string,
): UsePrivateRouteOutput => {
  const dispatch: AppDispatch = useDispatch();

  const { userDetails, permissions, isLoading, hasData, failedToLoad } =
    useSelector(authStoreStateSelector);
  const isAuthenticated = !!userDetails;

  const { setLanguage } = useSelectedLanguageContext();

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(fetchAuthenticatedUserDetails())
        .then(unwrapResult)
        .then(({ userDetails: { language } }) => {
          setLanguage(language.replace(/["']/g, ''));
        })
        .catch(() => {
          redirectToOrigin('Not authenticated');
        });
    }
  }, [dispatch, isAuthenticated, userDetails, setLanguage]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (
      !pathModuleIsEnabled(AppModules.UI2, permissions) ||
      authenticatedUserIsAdmin(userDetails)
    ) {
      redirectToOrigin('Module path not enabled');
    }

    if (
      path &&
      moduleRestrict &&
      !pathModuleIsEnabled(
        AppModules.WORKSPACE,
        permissions,
        path as keyof typeof PathModules,
      )
    ) {
      redirectToOrigin('Workspace not enabled');
    }
  }, [isAuthenticated, moduleRestrict, path, permissions, userDetails]);

  return { isAuthenticated, isLoading, hasData, failedToLoad };
};
